import type { RoundFetch } from '@/client/pages/btc-5m/api';
import { clockOffset } from '@/client/pages/btc-5m/clock';
import { isTradable, roundPhase, type Round, type Token } from '@/client/pages/btc-5m/model';
import { BASE_DELAY_MS, IDLE_DELAY_MS } from '@/client/pages/btc-5m/poll-schedule';
import type { Market, MarketDetail } from '@/libs/sdk/api';

// Display clock (seconds): device time moved by the feed's Date header offset, as on the 5-minute page.
export const feedNow = (feed: RoundFetch | undefined, updatedAt: number) =>
  Date.now() / 1000 + (feed ? clockOffset(feed, updatedAt, updatedAt) : 0);

/**
 * The round the home cell shows: the first one published for `token` (event id, ready, pool address)
 * that is still open for trading (before or in its window). The engine writes the pool address after
 * deploy but sets ready only after publish; before that the market detail is a 4xx. From endAt on a round is skipped even while the
 * engine still marks it open (its status flips on the next engine tick). The 5-minute page keeps its own
 * phase rules.
 */
export const tradingRound = (rounds: Round[], token: Token, now: number) =>
  rounds.find(
    r =>
      !!r.markets[token]?.eventId &&
      !!r.markets[token]?.ready &&
      !!r.markets[token]?.marketAddress &&
      now < r.endAt &&
      ['upcoming', 'open'].includes(roundPhase(r, now))
  );

/** LIVE chip: the round is in its window and still trades (isTradable closes at endAt - 1, as the pool). */
export const isLiveRound = (round: Round, token: Token, now: number) =>
  roundPhase(round, now) === 'open' && isTradable(round, token, now);

/** The round whose market for `token` is `address` (feed and API may differ in letter case). */
export const roundOfMarket = (rounds: Round[], token: Token, address: string) =>
  address ? rounds.find(r => r.markets[token]?.marketAddress?.toLowerCase() === address.toLowerCase()) : undefined;

/**
 * Market detail in the list-market shape EventCard reads (get-market-detail and get-markets name the
 * same fields differently). Market detail has no event title, category, creation time or numeric event
 * id: the engine titles the market like its event, so EventCard falls back to marketName and nameI18n;
 * without a category the card shows no chip but LIVE. The home page has no error boundary, so missing
 * fields degrade (no rows, empty names, base-token decimals) instead of throwing in the card.
 */
export const toEventCardMarket = (detail: MarketDetail, eventId: string): Market => ({
  marketAddress: detail.marketAddress,
  marketName: detail.marketName,
  nameI18n: detail.nameI18n,
  eventTitle: '',
  marketPicUrl: detail.marketPicUrl,
  marketDescription: detail.marketDescription,
  marketStatus: detail.marketStatus,
  marketVolume: detail.volume,
  // Undefined lets CurrencyView fall back to the base token decimals ("undefined" would print NaN as 0).
  marketDecimal: (detail.decimal == null ? undefined : String(detail.decimal)) as string,
  participantsCount: detail.participantsCount,
  options: (detail.optionList ?? []).map(option => ({
    decimal: option.decimal,
    tokenAddress: option.optionAddress,
    tokenDescription: option.description ?? '',
    tokenName: option.optionName ?? '',
    tokenPicUrl: option.optionPicUrl,
    tokenPrice: option.price,
  })),
  baseTokenAddress: detail.baseTokenAddress,
  isFollowed: detail.isFollowed,
  createdAt: 0,
  deadline: Number(detail.deadline),
  eventId,
  eventNumericId: 0,
  result: detail.resultOptionAddress,
});

/**
 * Next feed refresh: 1s after the next start or end of a round that is not final (the engine flips
 * status on its next tick), at least 5s and at most 30s apart. Once every round is final, 30s: the
 * engine may write the next batch.
 */
export const refreshDelay = (rounds: Round[], now: number) => {
  const live = rounds.filter(r => r.status !== 'resolved' && r.status !== 'cancelled');
  if (!live.length) return IDLE_DELAY_MS;
  const next = Math.min(...live.flatMap(r => [r.startAt, r.endAt]).filter(t => t > now));
  return Math.min(IDLE_DELAY_MS, Math.max(BASE_DELAY_MS, (next - now + 1) * 1000));
};
